// ── Entry point — replaces the IIFE wrapper from app.js ──

// Re-export module init functions used during bootstrap
import { e } from './modules/utils.js';
import { SESSION_KEY, SESSION_USER_KEY, initAuth, isAuthenticated, setSession, logout, softLogout, showOverlay, hideOverlay, switchAuthTab, getAuthHeader, apiAuth, setUsernameDisplay } from './modules/auth-client.js';
import { initStorage, loadOverrides, saveOverrides, cloudSave, cloudSync } from './modules/storage.js';
import { initCapture, openCapture, setupCaptureListeners } from './modules/capture.js';
import { initSearch, openSearch, closeSearch, setupSearchListeners } from './modules/search.js';
import { initDelegateClient, refreshDelegateQueue, updateCapActivityBadge, setupCapActivityPanel } from './modules/delegate-client.js';
import { initChatClient, initAiChatPanel } from './modules/chat-client.js';
import { initSettings, openSettings, closeSettings, setupSettingsListeners } from './modules/settings.js';
import { initDragDrop, saveCardOrder } from './modules/drag-drop.js';

import { boardData, navStack, setNavStack, expandedCards, _boardDataRef, _activeFocusTrapRef, _focusTrapTriggerRef, _localOverridesVersion, setLocalOverridesVersion, AI_NAME, setAiName, _cardFlagsTimer, setCardFlagsTimer } from './core.js';
import { trapFocus, clientIsStrongPassword, getPasswordStrength } from './ui.js';
import { verifyCookieSession, triggerManualRefresh, refreshCardFlags } from './auth.js';
import { loadBoard, mergeOverrides, findCardById } from './data.js';
import { flashSaved, flashToast } from './cards.js';
import { renderFromState } from './render.js';
import { setFocusMode, setReviewMode, setWinsMode, showReviewTab, renderWins, renderReviewStep, renderNextActions, syncToolbarActive, openIntentionsModal, stopIntentions, advanceIntention, enterWorkMode, exitWorkMode, updateReviewTooltip, setKbFocus, clearKbFocus, getKbCards, isFocusMode, isReviewMode, isWinsMode, populateNaFilters, enterFocusFlow, exitFocusFlow, isFocusFlowActive } from './views.js';
import { isMobileViewport, initMobile, onMobileRenderComplete } from './mobile.js';

// ── localStorage migration: kapboard_* → refocus_* ──
// Runs once at startup before any reads — preserves existing sessions and data.
const _legacySession = localStorage.getItem('kapboard_session');
if (_legacySession && !localStorage.getItem('refocus_session')) {
  localStorage.setItem('refocus_session', _legacySession);
  localStorage.removeItem('kapboard_session');
}
const _legacyUser = localStorage.getItem('kapboard_user');
if (_legacyUser && !localStorage.getItem('refocus_user')) {
  localStorage.setItem('refocus_user', _legacyUser);
  localStorage.removeItem('kapboard_user');
}
const _legacyOverrides = localStorage.getItem('kapboard_overrides');
if (_legacyOverrides && !localStorage.getItem('refocus_overrides')) {
  localStorage.setItem('refocus_overrides', _legacyOverrides);
  localStorage.removeItem('kapboard_overrides');
}
// Migrate per-user overrides: kapboard_overrides_${user} → refocus_overrides_${user}
const _migUser = localStorage.getItem('refocus_user') || 'default';
const _legacyUserOv = localStorage.getItem(`kapboard_overrides_${_migUser}`);
if (_legacyUserOv && !localStorage.getItem(`refocus_overrides_${_migUser}`)) {
  localStorage.setItem(`refocus_overrides_${_migUser}`, _legacyUserOv);
  localStorage.removeItem(`kapboard_overrides_${_migUser}`);
}
// Migrate user ID: kapboard_user_id → refocus_user_id
const _legacyUserId = localStorage.getItem('kapboard_user_id');
if (_legacyUserId && !localStorage.getItem('refocus_user_id')) {
  localStorage.setItem('refocus_user_id', _legacyUserId);
  localStorage.removeItem('kapboard_user_id');
}
// Migrate chat history: kapboard_chat_history_${userId} → refocus_chat_history_${userId}
const _migUserId = localStorage.getItem('refocus_user_id') || 'default';
const _legacyChatHist = localStorage.getItem(`kapboard_chat_history_${_migUserId}`);
if (_legacyChatHist && !localStorage.getItem(`refocus_chat_history_${_migUserId}`)) {
  localStorage.setItem(`refocus_chat_history_${_migUserId}`, _legacyChatHist);
  localStorage.removeItem(`kapboard_chat_history_${_migUserId}`);
}

// Migrate keyboard hint dismissed flag
const _legacyKbHint = localStorage.getItem('refocus_kb_hint_dismissed');
if (_legacyKbHint && !localStorage.getItem('refocus_kb_hint_dismissed')) {
  localStorage.setItem('refocus_kb_hint_dismissed', _legacyKbHint);
  localStorage.removeItem('kapboard_kb_hint_dismissed');
}

// ── Init ──
async function init() {
  // ── Initialize extracted modules ──
  initAuth({ boardDataRef: _boardDataRef });
  initStorage({
    getAuthHeader,
    getVersion: () => _localOverridesVersion,
    setVersion: (v) => { setLocalOverridesVersion(v); },
    getUserKey: () => localStorage.getItem(SESSION_USER_KEY) || 'default',
    softLogout,
    flashSaved,
    flashToast,
    renderFromState,
  });
  // Non-critical module inits — errors disable the feature but don't break the board
  function safeInit(name, fn) {
    try { fn(); } catch (err) {
      console.error(`[Re:Focus] ${name} init failed — feature disabled:`, err);
    }
  }

  safeInit('Capture', () => initCapture({
    getAuthHeader,
    getBoardData: () => boardData,
    getOverrides: loadOverrides,
    saveOverrides,
    renderFromState,
    mergeOverrides,
    loadOverrides,
    trapFocus,
    activeFocusTrapRef: _activeFocusTrapRef,
    focusTrapTriggerRef: _focusTrapTriggerRef,
    getAiName: () => AI_NAME,
  }));
  safeInit('Search', () => initSearch({
    getBoardData: () => boardData,
    mergeOverrides,
    loadOverrides,
    renderFromState,
    navToCard: (card, path) => {
      setNavStack([]);
      const d2 = mergeOverrides(boardData);
      // Rebuild navStack from card hierarchy by ID — not fragile name matching
      function findPath(cards, targetId, trail) {
        for (const c of cards) {
          if (c.id === targetId) return trail;
          if (c.children?.length) {
            const found = findPath(c.children, targetId, [...trail, { id: c.id, name: c.name }]);
            if (found) return found;
          }
        }
        return null;
      }
      const allCards = d2.columns.flatMap(col => col.projects);
      const navPath = findPath(allCards, card.id, []);
      if (navPath) {
        for (const entry of navPath) navStack.push(entry);
      }
      expandedCards.add(card.id);
      const { setPendingTaskFocusId } = require_core();
      setPendingTaskFocusId(card.id);
      renderFromState();
    },
    trapFocus,
    activeFocusTrapRef: _activeFocusTrapRef,
    focusTrapTriggerRef: _focusTrapTriggerRef,
  }));
  safeInit('Delegate', () => initDelegateClient({
    getAuthHeader,
    getBoardData: () => boardData,
    loadOverrides,
    saveOverrides,
    renderFromState,
    cloudSave,
    cloudSync,
    getAiName: () => AI_NAME,
    expandedCardsRef: { value: expandedCards },
    openWorkspace: (...args) => { const { openWorkspace } = require_views(); openWorkspace(...args); },
    triggerManualRefresh,
    lastActivityRef: { get value() { return typeof _lastActivity !== 'undefined' ? _lastActivity : Date.now(); } },
  }));
  safeInit('Chat', () => initChatClient({
    trapFocus,
    getAiName: () => AI_NAME,
  }));
  safeInit('Settings', () => initSettings({
    trapFocus,
    activeFocusTrapRef: _activeFocusTrapRef,
    focusTrapTriggerRef: _focusTrapTriggerRef,
    renderFromState,
    getAiName: () => AI_NAME,
    setAiName: (name) => { setAiName(name); },
  }));
  safeInit('DragDrop', () => initDragDrop({
    navStackRef: { get value() { return navStack; } },
  }));

  // ── Handle email verification link: /verify?token=... ──
  const _urlParams = new URLSearchParams(window.location.search);
  const _verifyToken = _urlParams.get('token');
  if (_verifyToken) {
    showOverlay();
    const loginErr = document.getElementById('login-error');
    const loginSuccess = document.getElementById('login-success');
    loginErr.classList.add('hidden');
    try {
      const resp = await fetch('/api/auth', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'verify-email', token: _verifyToken }),
      });
      const data = await resp.json();
      if (resp.ok) {
        loginSuccess.textContent = 'Email verified! You can now sign in.';
        loginSuccess.classList.remove('hidden');
      } else {
        loginErr.textContent = data.error || 'Verification failed';
        loginErr.classList.remove('hidden');
      }
    } catch {
      loginErr.textContent = 'Network error — could not verify';
      loginErr.classList.remove('hidden');
    }
    history.replaceState(null, '', window.location.pathname);
  } else {
    if (!isAuthenticated()) {
      // Check for valid httpOnly cookie session (user may have cleared localStorage)
      if (await verifyCookieSession()) {
        hideOverlay();
      } else {
        showOverlay();
      }
    } else {
      hideOverlay();
    }
  }

  // ── Auth tab switching ──
  document.querySelectorAll('.auth-tab').forEach(tab => {
    tab.addEventListener('click', () => switchAuthTab(tab.dataset.tab));
  });

  // ── Sign in ──
  document.getElementById('login-form').addEventListener('submit', async (e) => {
    e.preventDefault();
    const un = document.getElementById('login-username').value.trim();
    const pw = document.getElementById('login-password').value;
    const errEl = document.getElementById('login-error');
    const successEl = document.getElementById('login-success');
    const verifyNotice = document.getElementById('verify-notice');
    errEl.classList.add('hidden');
    successEl.classList.add('hidden');
    verifyNotice.classList.add('hidden');
    document.getElementById('resend-verify-form').classList.add('hidden');
    const btn = e.target.querySelector('button[type="submit"]');
    btn.disabled = true; btn.textContent = 'Signing in\u2026';
    try {
      const { ok, data } = await apiAuth('login', un, pw);
      if (ok) {
        setSession(data.token, data.displayName || data.username);
        hideOverlay();
        await loadBoard();
      } else if (data.unverified) {
        document.getElementById('verify-notice-text').textContent = 'Check your email to verify your account.';
        verifyNotice.classList.remove('hidden');
        document.getElementById('login-password').value = '';
      } else {
        errEl.textContent = data.error || 'Incorrect username or password';
        errEl.classList.remove('hidden');
        document.getElementById('login-password').value = '';
        document.getElementById('login-password').focus();
      }
    } catch {
      errEl.textContent = 'Network error \u2014 try again';
      errEl.classList.remove('hidden');
    }
    btn.disabled = false; btn.textContent = 'Sign in';
  });

  // ── Register ──
  document.getElementById('register-form').addEventListener('submit', async (e) => {
    e.preventDefault();
    const em = document.getElementById('reg-email').value.trim();
    const un = document.getElementById('reg-username').value.trim();
    const pw = document.getElementById('reg-password').value;
    const pw2 = document.getElementById('reg-confirm').value;
    const errEl = document.getElementById('register-error');
    errEl.classList.add('hidden');
    if (!em || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em)) {
      errEl.textContent = 'Please enter a valid email address';
      errEl.classList.remove('hidden');
      return;
    }
    if (!/^[a-zA-Z0-9_-]{3,30}$/.test(un)) {
      errEl.textContent = 'Username must be 3\u201330 characters: letters, numbers, _ or - only';
      errEl.classList.remove('hidden');
      return;
    }
    if (pw.length < 8) {
      errEl.textContent = 'Password must be at least 8 characters';
      errEl.classList.remove('hidden');
      return;
    }
    if (!clientIsStrongPassword(pw)) {
      errEl.textContent = 'Password must be at least 10 characters, or 8+ characters with uppercase, lowercase, and numbers.';
      errEl.classList.remove('hidden');
      return;
    }
    if (pw !== pw2) {
      errEl.textContent = 'Passwords don\'t match';
      errEl.classList.remove('hidden');
      return;
    }
    if (!document.getElementById('reg-tos').checked) {
      errEl.textContent = 'You must agree to the Terms of Service';
      errEl.classList.remove('hidden');
      return;
    }
    const btn = e.target.querySelector('button[type="submit"]');
    btn.disabled = true; btn.textContent = 'Creating\u2026';
    try {
      const { ok, data } = await apiAuth('register', un, pw, { email: em, tosVersion: 'beta-2026-03' });
      if (ok && data.needsVerification) {
        // Show verification message on the login tab
        switchAuthTab('login');
        const successEl = document.getElementById('login-success');
        successEl.textContent = 'Account created! Check your email to verify your account.';
        successEl.classList.remove('hidden');
      } else if (ok) {
        setSession(data.token, data.displayName || data.username);
        hideOverlay();
        await loadBoard();
      } else {
        errEl.textContent = data.error || 'Could not create account';
        errEl.classList.remove('hidden');
      }
    } catch {
      errEl.textContent = 'Network error \u2014 try again';
      errEl.classList.remove('hidden');
    }
    btn.disabled = false; btn.textContent = 'Create account';
  });

  // ── Password strength indicator ──
  document.getElementById('reg-password').addEventListener('input', (ev) => {
    const pw = ev.target.value;
    const bar = document.getElementById('pw-strength-bar');
    const fill = document.getElementById('pw-strength-fill');
    const hint = document.getElementById('pw-strength-hint');
    if (!pw) { bar.style.display = 'none'; hint.style.display = 'none'; return; }
    const s = getPasswordStrength(pw);
    bar.style.display = '';
    fill.style.width = (s.level * 25) + '%';
    fill.style.background = s.color;
    hint.style.display = '';
    hint.textContent = s.label;
    hint.style.color = s.color;
  });

  // ── ToS consent checkbox ──
  document.getElementById('reg-tos').addEventListener('change', (ev) => {
    document.getElementById('reg-submit-btn').disabled = !ev.target.checked;
  });

  // ── Resend verification email ──
  document.getElementById('resend-verify-btn').addEventListener('click', () => {
    document.getElementById('resend-verify-form').classList.remove('hidden');
    document.getElementById('resend-verify-email').focus();
  });
  document.getElementById('resend-verify-submit').addEventListener('click', async () => {
    const emailInput = document.getElementById('resend-verify-email');
    const statusEl = document.getElementById('resend-verify-status');
    const email = emailInput.value.trim();
    if (!email) return;
    statusEl.classList.add('hidden');
    try {
      await fetch('/api/auth', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'resend-verification', email }),
      });
      statusEl.textContent = 'If that email is registered, a new verification link has been sent.';
      statusEl.classList.remove('hidden');
    } catch {
      statusEl.textContent = 'Network error \u2014 try again';
      statusEl.classList.remove('hidden');
    }
  });

  document.getElementById('lock-btn').addEventListener('click', logout);
  document.getElementById('home-btn').addEventListener('click', () => {
    setNavStack([]);
    renderFromState();
  });

  // ── Account dropdown ──
  const _acctBtn = document.getElementById('account-menu-btn');
  const _acctDrop = document.getElementById('account-dropdown');
  _acctBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    _acctDrop.classList.toggle('hidden');
  });
  document.addEventListener('click', () => _acctDrop.classList.add('hidden'));

  // ── Forgot password flow ──
  document.getElementById('forgot-password-btn').addEventListener('click', () => {
    document.getElementById('login-form').classList.add('hidden');
    document.getElementById('register-form').classList.add('hidden');
    document.getElementById('forgot-form').classList.remove('hidden');
    document.querySelectorAll('.auth-tab').forEach(t => t.classList.remove('active'));
    document.getElementById('forgot-email').focus();
  });
  document.getElementById('forgot-back-btn').addEventListener('click', () => {
    document.getElementById('forgot-form').classList.add('hidden');
    switchAuthTab('login');
  });
  document.getElementById('forgot-form').addEventListener('submit', async (ev) => {
    ev.preventDefault();
    const email = document.getElementById('forgot-email').value.trim();
    const errEl = document.getElementById('forgot-error');
    const successEl = document.getElementById('forgot-success');
    errEl.classList.add('hidden');
    successEl.classList.add('hidden');
    if (!email) { errEl.textContent = 'Please enter your email'; errEl.classList.remove('hidden'); return; }
    const btn = ev.target.querySelector('button[type="submit"]');
    btn.disabled = true; btn.textContent = 'Sending\u2026';
    try {
      const { ok, data } = await apiAuth('forgot-password', null, null, { email });
      if (ok) {
        successEl.textContent = "If that email is registered, you'll receive a temporary password shortly.";
        successEl.classList.remove('hidden');
      } else {
        errEl.textContent = data.error || 'Something went wrong';
        errEl.classList.remove('hidden');
      }
    } catch {
      errEl.textContent = 'Network error \u2014 try again';
      errEl.classList.remove('hidden');
    }
    btn.disabled = false; btn.textContent = 'Send temporary password';
  });

  // ── Change password ──
  document.getElementById('chpw-save-btn').addEventListener('click', async () => {
    const current = document.getElementById('chpw-current').value;
    const newPw = document.getElementById('chpw-new').value;
    const confirm = document.getElementById('chpw-confirm').value;
    const statusEl = document.getElementById('chpw-status');
    statusEl.classList.add('hidden');
    if (!current || !newPw) {
      statusEl.textContent = 'All fields are required';
      statusEl.className = 'chpw-status chpw-error'; return;
    }
    if (newPw.length < 8) {
      statusEl.textContent = 'New password must be at least 8 characters';
      statusEl.className = 'chpw-status chpw-error'; return;
    }
    if (newPw !== confirm) {
      statusEl.textContent = 'New passwords don\'t match';
      statusEl.className = 'chpw-status chpw-error'; return;
    }
    const token = localStorage.getItem(SESSION_KEY);
    const btn = document.getElementById('chpw-save-btn');
    btn.disabled = true; btn.textContent = 'Saving\u2026';
    try {
      const resp = await fetch('/api/auth', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ action: 'change-password', currentPassword: current, newPassword: newPw }),
      });
      const data = await resp.json();
      if (resp.ok) {
        statusEl.textContent = 'Password changed successfully';
        statusEl.className = 'chpw-status chpw-success';
        document.getElementById('chpw-current').value = '';
        document.getElementById('chpw-new').value = '';
        document.getElementById('chpw-confirm').value = '';
      } else {
        statusEl.textContent = data.error || 'Failed to change password';
        statusEl.className = 'chpw-status chpw-error';
      }
    } catch {
      statusEl.textContent = 'Network error \u2014 try again';
      statusEl.className = 'chpw-status chpw-error';
    }
    btn.disabled = false; btn.textContent = 'Change Password';
  });

  // ── Export data ──
  document.getElementById('export-data-btn').addEventListener('click', async () => {
    const btn = document.getElementById('export-data-btn');
    btn.disabled = true; btn.textContent = 'Exporting\u2026';
    try {
      const token = localStorage.getItem(SESSION_KEY);
      const resp = await fetch('/api/v1/export', {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (resp.ok) {
        const blob = await resp.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = resp.headers.get('Content-Disposition')?.match(/filename="(.+)"/)?.[1] || 'refocus-export.json';
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      }
    } catch { /* best-effort */ }
    btn.disabled = false; btn.textContent = 'Export Data (JSON)';
  });

  // ── Report a bug ──
  document.getElementById('report-bug-btn')?.addEventListener('click', () => {
    const username = localStorage.getItem(SESSION_USER_KEY) || '(not logged in)';
    const appVersion = document.querySelector('.settings-version')?.textContent?.trim() || 'unknown';
    const ua = navigator.userAgent;
    const platform = navigator.platform || 'unknown';
    const url = window.location.href;
    const online = navigator.onLine ? 'Yes' : 'No';
    const subject = encodeURIComponent(`Bug Report [${appVersion}]`);
    const body = encodeURIComponent(
      `What happened:\n(describe the bug)\n\n` +
      `Steps to reproduce:\n1. \n2. \n3. \n\n` +
      `Expected result:\n\n` +
      `Actual result:\n\n` +
      `--- App info (auto-filled) ---\n` +
      `Version: ${appVersion}\n` +
      `Username: ${username}\n` +
      `URL: ${url}\n` +
      `Online: ${online}\n` +
      `Browser: ${ua}\n` +
      `Platform: ${platform}`
    );
    window.location.href = `mailto:hello@re-focus.app?subject=${subject}&body=${body}`;
  });

  // ── Sign out all other devices ──
  document.getElementById('signout-all-btn').addEventListener('click', async () => {
    const btn = document.getElementById('signout-all-btn');
    const statusEl = document.getElementById('signout-all-status');
    statusEl.classList.add('hidden');
    btn.disabled = true; btn.textContent = 'Signing out\u2026';
    try {
      const token = localStorage.getItem(SESSION_KEY);
      const resp = await fetch('/api/v1/sessions', {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (resp.ok) {
        statusEl.textContent = 'All other sessions have been signed out';
        statusEl.className = 'chpw-status chpw-success';
      } else {
        statusEl.textContent = 'Failed to sign out other sessions';
        statusEl.className = 'chpw-status chpw-error';
      }
    } catch {
      statusEl.textContent = 'Network error \u2014 try again';
      statusEl.className = 'chpw-status chpw-error';
    }
    btn.disabled = false; btn.textContent = 'Sign out all other devices';
  });

  // ── Delete account ──
  const deleteInput = document.getElementById('delete-confirm-input');
  const deleteBtn = document.getElementById('delete-account-btn');
  deleteInput.addEventListener('input', () => {
    deleteBtn.disabled = deleteInput.value.trim() !== 'DELETE';
  });
  deleteBtn.addEventListener('click', async () => {
    if (deleteInput.value.trim() !== 'DELETE') return;
    deleteBtn.disabled = true; deleteBtn.textContent = 'Deleting\u2026';
    const token = localStorage.getItem(SESSION_KEY);
    try {
      const resp = await fetch('/api/v1/account', {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (resp.ok) {
        localStorage.clear();
        closeSettings();
        showOverlay();
      } else {
        const data = await resp.json();
        alert(data.error || 'Failed to delete account');
      }
    } catch {
      alert('Network error \u2014 try again');
    }
    deleteBtn.disabled = false; deleteBtn.textContent = 'Delete my account';
  });

  // ── Settings (extracted → settings.js) ──
  setupSettingsListeners();

  // ── MCP snippet copy button ──
  document.getElementById("copy-mcp-btn")?.addEventListener("click", () => {
    const text = document.getElementById("mcp-snippet").textContent;
    navigator.clipboard.writeText(text).then(() => {
      const btn = document.getElementById("copy-mcp-btn");
      btn.textContent = "Copied!";
      setTimeout(() => btn.textContent = "Copy", 2000);
    });
  });

  // ── Keyboard shortcuts modal ──
  document.getElementById("shortcuts-btn")?.addEventListener("click", () => {
    document.getElementById("shortcuts-modal").classList.toggle("hidden");
  });
  document.getElementById("shortcuts-close")?.addEventListener("click", () => {
    document.getElementById("shortcuts-modal").classList.add("hidden");
  });

  // ── Cap activity panel — imported from delegate-client.js ──
  const _capPanel = setupCapActivityPanel();
  const { closeCapActivity } = _capPanel;

  // Idle detection — skip polling when user hasn't interacted for >5 minutes
  let _lastActivity = Date.now();
  const _trackActivity = () => { _lastActivity = Date.now(); };
  document.addEventListener("keydown", _trackActivity, { passive: true });
  document.addEventListener("click", _trackActivity, { passive: true });
  document.addEventListener("touchstart", _trackActivity, { passive: true });

  // Update badge after delegate queue refresh
  const _origRefreshDelegate2 = refreshDelegateQueue;
  const _wrappedRefreshDelegate = async function() {
    await _origRefreshDelegate2();
    updateCapActivityBadge();
  };

  // Poll every 30 seconds (skip when tab is backgrounded or user idle >5 min)
  setInterval(() => {
    if (document.hidden) return;
    if (Date.now() - _lastActivity > 5 * 60 * 1000) return;
    cloudSync(); _wrappedRefreshDelegate();
  }, 30 * 1000);

  // ── Search (⌘K) — imported from search.js ──
  setupSearchListeners();

  // Overview back button (Next Actions mode)
  document.getElementById('overview-back-btn').addEventListener('click', () => setFocusMode(false));

  // Keyboard hint — show once on first J/K use, dismiss on click
  (function() {
    const hint = document.getElementById('kb-hint');
    const dismiss = document.getElementById('kb-hint-dismiss');
    let shown = localStorage.getItem('refocus_kb_hint_dismissed');
    if (!shown) {
      document.addEventListener('keydown', function showHint(e) {
        if ((e.key === 'j' || e.key === 'k') && !shown) {
          hint.classList.add('visible');
          shown = true;
          document.removeEventListener('keydown', showHint);
        }
      });
    }
    dismiss.addEventListener('click', () => {
      hint.classList.remove('visible');
      localStorage.setItem('refocus_kb_hint_dismissed', '1');
    });
  })();

  // ── Capture modal (imported from capture.js) ──
  setupCaptureListeners();

  // ── Wins panel ──
  document.getElementById('wins-clear-btn').addEventListener('click', () => {
    if (!confirm('Clear all wins? This cannot be undone.')) return;
    const ov = loadOverrides();
    ov.completedTasks = [];
    saveOverrides(ov);
    renderWins();
  });

  document.getElementById('review-btn').addEventListener('click', () => {
    if (isReviewMode()) setReviewMode(false);
    else setReviewMode(true);
  });

  document.getElementById('tab-wins').addEventListener('click', () => {
    showReviewTab('wins');
    renderWins();
    syncToolbarActive();
  });
  document.getElementById('tab-review').addEventListener('click', () => {
    showReviewTab('review');
    renderReviewStep();
    syncToolbarActive();
  });

  updateReviewTooltip();

  // ── Work mode exit ──
  document.getElementById('work-exit-btn').addEventListener('click', exitWorkMode);

  // Keyboard shortcuts for work mode
  document.addEventListener('keydown', (evt) => {
    // Work mode check handled by views.js internal state
    if (!document.getElementById('work-mode').classList.contains('hidden')) {
      if (evt.key === 'Escape') { exitWorkMode(); evt.preventDefault(); return; }
      if (evt.key === ' ' || evt.key === 'Enter') {
        const tag = document.activeElement.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
        document.getElementById('work-done-btn').click();
        evt.preventDefault();
        return;
      }
    }
  });

  // ── Intentions ──
  document.getElementById('intentions-btn').addEventListener('click', () => {
    const { isIntentionsActive } = require_views();
    if (isIntentionsActive()) {
      stopIntentions();
      document.getElementById('intentions-btn').innerHTML = '\ud83c\udfaf Intentions';
    } else {
      openIntentionsModal();
    }
  });
  document.getElementById('intentions-stop-btn').addEventListener('click', stopIntentions);
  document.getElementById('intentions-advance-btn').addEventListener('click', () => advanceIntention(false));
  document.getElementById('intentions-stay-btn').addEventListener('click', () => {
    // Handled internally by views.js
    document.getElementById('intentions-transition').classList.add('hidden');
  });

  // ── Next Actions ──
  document.getElementById('next-actions-btn').addEventListener('click', () => setFocusMode(!isFocusMode()));
  document.getElementById('na-column-filter').addEventListener('change', renderNextActions);
  document.getElementById('na-count').addEventListener('change', renderNextActions);

  // ── Keyboard shortcut: C to open capture (when not in input) ──
  // ── Keyboard navigation ──
  let _kbCardIdx = -1;

  document.addEventListener('keydown', (e) => {
    const tag = document.activeElement.tagName;
    const isTyping = tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || document.activeElement.isContentEditable;
    const modalOpen = !document.getElementById('capture-modal').classList.contains('hidden');
    const searchOpen = !document.getElementById('search-modal').classList.contains('hidden');

    if (e.key === 'Escape') {
      const shortcutsOpen = !document.getElementById('shortcuts-modal').classList.contains('hidden');
      if (shortcutsOpen) { document.getElementById('shortcuts-modal').classList.add('hidden'); return; }
      const settingsOpen = !document.getElementById('settings-modal').classList.contains('hidden');
      if (settingsOpen) { closeSettings(); return; }
      if (searchOpen) { closeSearch(); return; }
      if (modalOpen) { closeCapture(); return; }
      if (isFocusFlowActive()) { exitFocusFlow(); return; }
      if (isFocusMode()) { setFocusMode(false); return; }
      if (isWinsMode()) { setWinsMode(false); return; }
      if (isReviewMode()) { setReviewMode(false); return; }
      clearKbFocus();
      return;
    }

    // ? — keyboard shortcuts modal (not while typing, no modifier keys)
    if (e.key === '?' && !e.ctrlKey && !e.metaKey && !isTyping) {
      document.getElementById('shortcuts-modal')?.classList.toggle('hidden');
      return;
    }

    // N — capture (not while typing; bare key only, no cmd combos)
    if (!isTyping && !modalOpen) {
      if (e.key === 'n' || e.key === 'N') { e.preventDefault(); openCapture(); return; }
      // / or Cmd+K — search
      if (e.key === '/' || ((e.metaKey||e.ctrlKey) && e.key === 'k')) { e.preventDefault(); openSearch(); return; }
      // B — return to main board view
      if (e.key === 'b' || e.key === 'B') {
        e.preventDefault();
        setReviewMode(false);
        setFocusMode(false);
        setWinsMode(false);
        return;
      }
    }

    // Board navigation — J/K/Enter only when board visible and not typing
    if (!isTyping && !modalOpen && !isFocusMode() && !isReviewMode() && !isWinsMode()) {
      if (e.key === 'j' || e.key === 'J') {
        e.preventDefault();
        setKbFocus(_kbCardIdx < 0 ? 0 : _kbCardIdx + 1);
        return;
      }
      if (e.key === 'k' || e.key === 'K') {
        e.preventDefault();
        setKbFocus(_kbCardIdx <= 0 ? 0 : _kbCardIdx - 1);
        return;
      }
      if (e.key === 'Enter' && _kbCardIdx >= 0) {
        e.preventDefault();
        const cards = getKbCards();
        if (cards[_kbCardIdx]) {
          const projId = cards[_kbCardIdx].dataset.projId;
          if (projId) {
            if (expandedCards.has(projId)) { expandedCards.delete(projId); }
            else { expandedCards.add(projId); }
            renderFromState();
            setTimeout(() => setKbFocus(_kbCardIdx), 50);
          }
        }
        return;
      }
      // N — add task to focused card
      if ((e.key === 'n' || e.key === 'N') && _kbCardIdx >= 0) {
        e.preventDefault();
        const cards = getKbCards();
        const projId = cards[_kbCardIdx]?.dataset.projId;
        if (projId) {
          if (!expandedCards.has(projId)) { expandedCards.add(projId); renderFromState(); }
          setTimeout(() => {
            const inp = document.querySelector(`.add-task-input[data-project-id="${projId}"]`);
            if (inp) inp.focus();
          }, 80);
        }
        return;
      }
    }
  });

  if (isAuthenticated()) {
    await loadBoard();
    // Fire-and-forget: load card flags + refresh every 10 minutes
    refreshCardFlags().then(() => renderFromState());
    setCardFlagsTimer(setInterval(refreshCardFlags, 10 * 60 * 1000));
    refreshDelegateQueue();
    // Init mobile shell if applicable
    initMobile();

    // Focus Flow startup preference (§9b)
    const startupOv = loadOverrides();
    updateStartupToggle(startupOv.startupView || 'overview');
    if (startupOv.startupView === 'focus' && !isMobileViewport()) {
      enterFocusFlow();
    }

    // First-login welcome banner
    const WELCOMED_KEY = `refocus_welcomed_${localStorage.getItem(SESSION_USER_KEY) || "user"}`;
    if (!localStorage.getItem(WELCOMED_KEY)) {
      const banner = document.getElementById("welcome-banner");
      if (banner) banner.classList.remove("hidden");
      document.getElementById("welcome-dismiss")?.addEventListener("click", () => {
        banner?.classList.add("hidden");
        localStorage.setItem(WELCOMED_KEY, "1");
      });
      // Auto-dismiss after 12 seconds
      setTimeout(() => {
        banner?.classList.add("hidden");
        localStorage.setItem(WELCOMED_KEY, "1");
      }, 12000);
    }
  }
}

// ── Focus Flow toggle (§9b) ──
function updateStartupToggle(view) {
  document.querySelectorAll('.startup-toggle-opt').forEach(btn => {
    btn.classList.toggle('active', btn.dataset.view === view);
  });
}

document.getElementById('startup-toggle')?.addEventListener('click', (evt) => {
  const btn = evt.target.closest('.startup-toggle-opt');
  if (!btn || btn.classList.contains('active')) return;
  const view = btn.dataset.view;
  const ov = loadOverrides();
  ov.startupView = view;
  saveOverrides(ov);
  updateStartupToggle(view);
  if (view === 'focus') {
    enterFocusFlow();
  } else {
    if (isFocusFlowActive()) exitFocusFlow();
  }
});

// ── Responsive layout switching (≤520px → mobile, >520px → desktop) ──
let _kapboardMode = window.innerWidth <= 520 ? 'mobile' : 'desktop';
let _modeDebounce;
window.addEventListener('resize', () => {
  clearTimeout(_modeDebounce);
  _modeDebounce = setTimeout(() => {
    const newMode = window.innerWidth <= 520 ? 'mobile' : 'desktop';
    if (newMode === _kapboardMode) return;
    _kapboardMode = newMode;
    if (newMode === 'mobile') {
      document.getElementById('mobile-shell').style.display = 'flex';
      initMobile();
      if (boardData) { onMobileRenderComplete(); }
    } else {
      document.getElementById('mobile-shell').style.display = '';
      if (boardData) renderFromState();
    }
  }, 50);
});

// ── Offline / Online banner ──
window.addEventListener('offline', () => {
  const banner = document.getElementById('offline-banner');
  if (banner) banner.classList.remove('hidden');
});
window.addEventListener('online', () => {
  const banner = document.getElementById('offline-banner');
  if (banner) banner.classList.add('hidden');
  // Re-sync when coming back online
  cloudSave(loadOverrides());
});
if (!navigator.onLine) {
  const banner = document.getElementById('offline-banner');
  if (banner) banner.classList.remove('hidden');
}

// ── Agent notifications (consolidated into Cap Activity panel) ──
const _agentNotifSection = document.getElementById('agent-notif-section');
const _agentNotifBody = document.getElementById('agent-notif-body');
const _capBadge = document.getElementById('cap-activity-badge');

async function _refreshAgentNotifs() {
  try {
    const resp = await fetch('/api/v1/agent-notifications', { headers: getAuthHeader() });
    if (!resp.ok) return;
    const data = await resp.json();
    const notifs = data.notifications || [];

    // Show/hide section
    if (_agentNotifSection) {
      _agentNotifSection.classList.toggle('hidden', notifs.length === 0);
    }

    // Update Cap Activity badge (add notification count to existing artifact count)
    if (_capBadge && notifs.length > 0) {
      const existing = parseInt(_capBadge.textContent) || 0;
      const artifactCount = _capBadge.dataset.artifactCount ? parseInt(_capBadge.dataset.artifactCount) : existing;
      const total = artifactCount + notifs.length;
      _capBadge.textContent = total;
      _capBadge.classList.remove('hidden');
    }

    // Render notifications
    if (_agentNotifBody) {
      _agentNotifBody.innerHTML = notifs.map(n => `
        <div class="agent-notif-card" data-notif-id="${n.id}">
          <button class="agent-notif-dismiss" title="Dismiss">×</button>
          <div class="agent-notif-event">${n.event || 'observation'}</div>
          <div class="agent-notif-message">${n.message || ''}</div>
          <div class="agent-notif-time">${n.createdAt ? new Date(n.createdAt).toLocaleString() : ''}</div>
        </div>
      `).join('');

      _agentNotifBody.querySelectorAll('.agent-notif-dismiss').forEach(btn => {
        btn.addEventListener('click', async (ev) => {
          const card = ev.target.closest('.agent-notif-card');
          const id = card?.dataset.notifId;
          if (!id) return;
          await fetch(`/api/v1/agent-notifications?id=${id}`, { method: 'DELETE', headers: getAuthHeader() });
          card.remove();
          if (_agentNotifBody.querySelectorAll('.agent-notif-card').length === 0) {
            _agentNotifSection?.classList.add('hidden');
          }
          _refreshAgentNotifs(); // re-count badge
        });
      });
    }
  } catch { /* offline */ }
}

// Poll for notifications every 60s (skip when hidden/idle)
setInterval(() => {
  if (document.hidden) return;
  if (!isAuthenticated()) return;
  _refreshAgentNotifs();
}, 60000);

// Initial check after board loads
setTimeout(() => { if (isAuthenticated()) _refreshAgentNotifs(); }, 3000);

// Close column menus when clicking outside
document.addEventListener('click', () => {
  document.querySelectorAll('.col-menu').forEach(m => m.classList.add('hidden'));
});

// ── Lazy import helpers (for avoiding circular dep issues at module evaluation time) ──
function require_core() {
  return { setPendingTaskFocusId: (v) => { import('./core.js').then(m => m.setPendingTaskFocusId(v)); } };
}
function require_views() {
  // These are already imported at top level, just return them
  return { openWorkspace: (...args) => { import('./views.js').then(m => m.openWorkspace(...args)); }, isIntentionsActive: () => { return false; } };
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}

// ── AI Chat Panel (extracted → chat-client.js) ──
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAiChatPanel);
} else {
  initAiChatPanel();
}
